import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useSystem } from './SystemContext';
import { useAccountOptional } from './AccountContext';
import {
  featureGatingService,
  type FeatureCheckResult,
  type GatingGate,
  type GatingGateRequest,
} from '../lib/featureGating';
import type { PlanDefinition, TemplateAccessTier } from '../lib/billing/types';

type Gate = GatingGate;

interface FeatureGatingContextValue {
  plans: PlanDefinition[];
  planId: string;
  loading: boolean;
  refresh: () => Promise<void>;
  check: (gate: Gate, options?: { templateTier?: TemplateAccessTier }) => Promise<FeatureCheckResult>;
  checkGates: (requests: GatingGateRequest[]) => Promise<FeatureCheckResult[]>;
  assertAllowed: (
    gate: Gate,
    options?: { templateTier?: TemplateAccessTier },
  ) => Promise<FeatureCheckResult>;
  recordUsage: (field: 'aiDrafts' | 'documentsCreated') => Promise<void>;
}

const FeatureGatingContext = createContext<FeatureGatingContextValue | null>(null);

export function FeatureGatingProvider({ children }: { children: React.ReactNode }) {
  const { user, userData, isAdmin } = useAuth();
  const accountCtx = useAccountOptional();
  const { config } = useSystem();
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [loading, setLoading] = useState(true);

  const planId = useMemo(
    () =>
      accountCtx?.planId ??
      featureGatingService.resolveUserPlanId(userData as Record<string, unknown>),
    [accountCtx?.planId, userData],
  );

  const refresh = useCallback(async () => {
    if (!user) {
      setPlans([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setPlans(await featureGatingService.loadPlanCatalog());
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const authContext = useMemo(
    () => ({
      isAdmin,
      email: user?.email,
      role: userData?.role as string | undefined,
    }),
    [isAdmin, user?.email, userData?.role],
  );

  const check = useCallback(
    async (gate: Gate, options?: { templateTier?: TemplateAccessTier }) => {
      if (!user?.uid) {
        return { allowed: false, reason: 'Sign in required.' };
      }
      if (gate === 'lexCounsel' || gate === 'aiDraft') {
        if (!config.enableAIFeatures) {
          return { allowed: false, reason: 'AI features are temporarily disabled platform-wide.' };
        }
      }
      return featureGatingService.check(
        user.uid,
        userData as Record<string, unknown>,
        authContext,
        gate,
        options,
        plans,
      );
    },
    [user, userData, authContext, config.enableAIFeatures, plans],
  );

  const checkGates = useCallback(
    async (requests: GatingGateRequest[]) => {
      if (!user?.uid) {
        return requests.map(() => ({ allowed: false, reason: 'Sign in required.' }));
      }
      for (const { gate } of requests) {
        if (gate === 'lexCounsel' || gate === 'aiDraft') {
          if (!config.enableAIFeatures) {
            return requests.map(() => ({
              allowed: false,
              reason: 'AI features are temporarily disabled platform-wide.',
            }));
          }
          break;
        }
      }
      return featureGatingService.checkGates(
        user.uid,
        userData as Record<string, unknown>,
        authContext,
        requests,
        plans,
      );
    },
    [user, userData, authContext, config.enableAIFeatures, plans],
  );

  const assertAllowed = useCallback(
    async (gate: Gate, options?: { templateTier?: TemplateAccessTier }) => {
      return check(gate, options);
    },
    [check],
  );

  const recordUsage = useCallback(
    async (field: 'aiDrafts' | 'documentsCreated') => {
      if (!user?.uid || isAdmin) return;
      await featureGatingService.incrementUsage(user.uid, field);
    },
    [user, isAdmin],
  );

  const value = useMemo(
    () => ({
      plans,
      planId,
      loading,
      refresh,
      check,
      checkGates,
      assertAllowed,
      recordUsage,
    }),
    [plans, planId, loading, refresh, check, checkGates, assertAllowed, recordUsage],
  );

  return (
    <FeatureGatingContext.Provider value={value}>{children}</FeatureGatingContext.Provider>
  );
}

export function useFeatureGating() {
  const ctx = useContext(FeatureGatingContext);
  if (!ctx) {
    throw new Error('useFeatureGating must be used within FeatureGatingProvider');
  }
  return ctx;
}

/** Safe hook for admin-only or optional trees */
export function useFeatureGatingOptional() {
  return useContext(FeatureGatingContext);
}

/**
 * Like useFeatureGating but never throws — falls back to direct service calls when
 * the provider is missing (e.g. partial HMR during dev).
 */
export function useFeatureGatingSafe(): FeatureGatingContextValue {
  const ctx = useContext(FeatureGatingContext);
  const { user, userData, isAdmin } = useAuth();
  const { config } = useSystem();

  const fallbackCheck = useCallback(
    async (gate: Gate, options?: { templateTier?: TemplateAccessTier }) => {
      if (!user?.uid) {
        return { allowed: false, reason: 'Sign in required.' };
      }
      if (gate === 'lexCounsel' || gate === 'aiDraft') {
        if (!config.enableAIFeatures) {
          return { allowed: false, reason: 'AI features are temporarily disabled platform-wide.' };
        }
      }
      return featureGatingService.check(
        user.uid,
        userData as Record<string, unknown>,
        { isAdmin, email: user.email, role: userData?.role as string },
        gate,
        options,
      );
    },
    [user, userData, isAdmin, config.enableAIFeatures],
  );

  const fallbackRecordUsage = useCallback(
    async (field: 'aiDrafts' | 'documentsCreated') => {
      if (!user?.uid || isAdmin) return;
      await featureGatingService.incrementUsage(user.uid, field);
    },
    [user, isAdmin],
  );

  const planId = useMemo(
    () =>
      ctx?.planId ??
      featureGatingService.resolveUserPlanId(userData as Record<string, unknown>),
    [ctx?.planId, userData],
  );

  const fallbackCheckGates = useCallback(
    async (requests: GatingGateRequest[]) => {
      if (!user?.uid) {
        return requests.map(() => ({ allowed: false, reason: 'Sign in required.' }));
      }
      for (const { gate } of requests) {
        if (gate === 'lexCounsel' || gate === 'aiDraft') {
          if (!config.enableAIFeatures) {
            return requests.map(() => ({
              allowed: false,
              reason: 'AI features are temporarily disabled platform-wide.',
            }));
          }
          break;
        }
      }
      return featureGatingService.checkGates(
        user.uid,
        userData as Record<string, unknown>,
        { isAdmin, email: user.email, role: userData?.role as string },
        requests,
      );
    },
    [user, userData, isAdmin, config.enableAIFeatures],
  );

  return useMemo(
    () => ({
      plans: ctx?.plans ?? [],
      planId,
      loading: ctx?.loading ?? false,
      refresh: ctx?.refresh ?? (async () => {}),
      check: ctx?.check ?? fallbackCheck,
      checkGates: ctx?.checkGates ?? fallbackCheckGates,
      assertAllowed: ctx?.assertAllowed ?? fallbackCheck,
      recordUsage: ctx?.recordUsage ?? fallbackRecordUsage,
    }),
    [ctx, planId, fallbackCheck, fallbackCheckGates, fallbackRecordUsage],
  );
}
